import { projectId } from '../utils/supabase/info';
import { Staff, Patient, NewPatientData, NewStaffData, AppointmentValidationError, AppointmentError, AppointmentRow, AppointmentRowInput, Appointment, Handover, ClinicSpeechTerm } from '../types';
import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from './supabaseClient';



const API_BASE_URL = `https://${projectId}.supabase.co/functions/v1/smart-worker`;

export type StaffAuthStatus = {
  email: string | null;
  isLinked: boolean;
  requiresPasswordSetup: boolean;
  isLoginEnabled?: boolean;
  pendingReason?: 'initial_invite' | 'email_change' | null;
};

// =========================================================
// Audit logs (operation_logs)
// =========================================================
type OperationLogInput = {
  clinicId: string;
  action: string; // e.g. 'patient.create', 'handover.update'
  targetType?: string; // e.g. 'patients','handovers','appointments'
  targetId?: string; // string to support non-uuid ids
  metadata?: Record<string, any>;
  // Always the authenticated Supabase user (uuid). If omitted, derived from current session.
  authUserId?: string;
  // Optional: operator selected via PIN in a device session (uuid staff.id)
  actedStaffId?: string | null;
};

export type StaffDeletePreview = {
  futureAppointmentsCount: number;
};

export type StaffDeleteResult = {
  success: boolean;
  error?: string;
};

const getStaffSnapshotName = async (staffId: string | null | undefined): Promise<string | null> => {
  if (!staffId) return null;
  const { data, error } = await supabase
    .from('staff')
    .select('name')
    .eq('id', staffId)
    .maybeSingle();
  if (error) {
    console.warn('staff snapshot lookup failed:', error);
    return null;
  }
  return data?.name ?? null;
};

export const insertOperationLog = async (input: OperationLogInput): Promise<void> => {
  try {
    const { clinicId, action, targetType, targetId, metadata, actedStaffId } = input;
    if (!clinicId) return;
    if (!action) return;

    let authUserId = input.authUserId;
    if (!authUserId) {
      const { data } = await supabase.auth.getSession();
      authUserId = data.session?.user?.id ?? undefined;
    }
    if (!authUserId) return;

    const actedStaffName = actedStaffId ? await getStaffSnapshotName(actedStaffId) : null;

    const row = {
      clinic_id: clinicId,
      auth_user_id: authUserId,
      acted_staff_id: actedStaffId ?? null,
      acted_staff_name: actedStaffName,
      action,
      target_type: targetType ?? null,
      target_id: targetId ?? null,
      metadata: metadata ?? {},
    };

    const { error } = await supabase.from('operation_logs').insert(row);
    if (error) {
      // Never block the main operation on audit log failure.
      console.warn('operation_logs insert failed:', error);
    }
  } catch (e) {
    console.warn('operation_logs insert error:', e);
  }
};

/**
 * 全てのスタッフ情報をデータベースから取得する
 */
export const getAllStaff = async (clinicId?: string): Promise<Staff[]> => {
  // 有効なスタッフのみ取得（無効化=ソフト削除済は除外）
  let query = supabase
    .from('staff')
    .select('*')
    .eq('is_active', true);
  if (clinicId) {
    query = query.eq('clinic_id', clinicId);
  }
  const { data, error } = await query;
  
  if (error) {
    console.error('スタッフ情報の取得に失敗しました:', error);
    return [];
  }
  
  // snake_case から camelCase への変換
  return data.map(staff => ({
    id: staff.id,
    createdAt: staff.created_at,
    updatedAt: staff.updated_at,
    name: staff.name,
    nameRomaji: staff.name_romaji,
    role: staff.role,
    pin: staff.pin,
    jobTitle: staff.job_title,
    phoneNumber: staff.phone_number || '',
    authUserId: staff.auth_user_id ?? null,
    isActive: staff.is_active,
    createdBy: staff.created_by,
    createdByName: staff.created_by_name || staff.created_by || '不明',
    updatedBy: staff.updated_by,
    updatedByName: staff.updated_by_name || staff.updated_by || '不明',
  }));
};

/**
 * 特定のスタッフの情報を更新する
 * @param staffId 更新するスタッフのID
 * @param updatedData 更新するデータ
 */
export const updateStaff = async (staffId: string, updatedData: Partial<Staff>): Promise<boolean> => {
  // camelCase から snake_case への変換
  const dbData: { [key: string]: any } = {};
  if (updatedData.name !== undefined) dbData.name = updatedData.name;
  if (updatedData.nameRomaji !== undefined) dbData.name_romaji = updatedData.nameRomaji;
  if (updatedData.pin !== undefined) dbData.pin = updatedData.pin;
  if (updatedData.role !== undefined) dbData.role = updatedData.role;
  if (updatedData.jobTitle !== undefined) dbData.job_title = updatedData.jobTitle;
  if (updatedData.phoneNumber !== undefined) dbData.phone_number = updatedData.phoneNumber;
  if (updatedData.isActive !== undefined) dbData.is_active = updatedData.isActive;
  if (updatedData.updatedBy !== undefined) dbData.updated_by = updatedData.updatedBy;
  if (updatedData.updatedByName !== undefined) {
    dbData.updated_by_name = updatedData.updatedByName;
  } else if (updatedData.updatedBy !== undefined) {
    const snapshotName = await getStaffSnapshotName(updatedData.updatedBy);
    if (snapshotName) {
      dbData.updated_by_name = snapshotName;
    }
  }
  dbData.updated_at = new Date().toISOString(); // 更新日は必ず更新

  const { error } = await supabase
    .from('staff')
    .update(dbData)
    .eq('id', staffId);

  if (error) {
    console.error('スタッフ情報の更新に失敗しました:', error);
    return false;
  }
  
  return true;
};

/**
 * 新しいスタッフをデータベースに登録する
 * @param newStaffData 登録するスタッフのデータ
 */
export const addStaff = async (
  newStaffData: NewStaffData,
  currentUserId: string,
  clinicId?: string
): Promise<Staff | null> => {
  // If email is provided, prefer server-side invite/linking flow (Supabase Auth required).
  // Caller may supply email to enable personal login (e.g. smartphone).
  if (newStaffData.email && clinicId) {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/invite-staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          clinicId,
          email: newStaffData.email,
          // When the current Auth user is a device, this identifies the operator.
          actedStaffId: currentUserId,
          staff: {
            name: newStaffData.name,
            nameRomaji: newStaffData.nameRomaji,
            role: newStaffData.role,
            pin: newStaffData.pin,
            jobTitle: newStaffData.jobTitle,
            phoneNumber: newStaffData.phoneNumber,
            createdBy: currentUserId,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = errorText || 'Staff invite failed';
        try {
          const parsed = JSON.parse(errorText);
          if (parsed?.error) {
            errorMessage = String(parsed.error);
          }
        } catch {
          // Keep the raw response text when JSON parsing is not possible.
        }
        throw new Error(errorMessage || 'Staff invite failed');
      }

      const result = await response.json();
      // Expect { staff } in response
      return (result?.staff as Staff) || null;
    } catch (e) {
      console.error('addStaff invite flow failed:', e);
      throw e instanceof Error ? e : new Error('Staff invite failed');
    }
  }

  const dbData = {
    name: newStaffData.name,
    name_romaji: newStaffData.nameRomaji,
    pin: newStaffData.pin,
    role: newStaffData.role,
    job_title: newStaffData.jobTitle,
    phone_number: newStaffData.phoneNumber,
    created_by: currentUserId,
    created_by_name: await getStaffSnapshotName(currentUserId),
    updated_by: currentUserId,
    updated_by_name: await getStaffSnapshotName(currentUserId),
    is_active: true,
    ...(clinicId ? { clinic_id: clinicId } : {}),
  };

  const { data, error } = await supabase
    .from('staff')
    .insert(dbData)
    .select()
    .single();

  if (error) {
    console.error('スタッフの追加に失敗しました:', error);
    console.error('エラー詳細:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code
    });
    return null;
  }
  
  // snake_case から camelCase への変換
  const result: Staff = {
    id: data.id,
    name: data.name,
    nameRomaji: data.name_romaji,
    pin: data.pin,
    role: data.role,
    jobTitle: data.job_title,
    phoneNumber: data.phone_number || '',
    authUserId: data.auth_user_id ?? null,
    createdAt: data.created_at,
    createdBy: data.created_by,
    createdByName: data.created_by_name || data.created_by || '不明',
    updatedAt: data.updated_at,
    updatedBy: data.updated_by,
    updatedByName: data.updated_by_name || data.updated_by || '不明',
    isActive: data.is_active,
  };

  return result;
};

/**
 * 既存スタッフに「スマホ個人ログイン用のAuth」を紐づける（招待メールを送信）
 */
export const linkStaffAuth = async (input: {
  clinicId: string;
  staffId: string;
  email: string;
  actedStaffId?: string;
}): Promise<boolean> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/link-staff-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        clinicId: input.clinicId,
        staffId: input.staffId,
        email: input.email,
        actedStaffId: input.actedStaffId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'linkStaffAuth failed');
    }

    return true;
  } catch (e) {
    console.error('linkStaffAuth failed:', e);
    return false;
  }
};

export const getStaffAuthStatus = async (input: {
  clinicId: string;
  staffId: string;
  actedStaffId?: string;
}): Promise<StaffAuthStatus | null> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/staff-auth-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'staff-auth-status failed');
    }

    return (await response.json()) as StaffAuthStatus;
  } catch (e) {
    console.error('getStaffAuthStatus failed:', e);
    return null;
  }
};

export const resendStaffInvite = async (input: {
  clinicId: string;
  staffId: string;
  actedStaffId?: string;
}): Promise<boolean> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/resend-staff-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'resend-staff-invite failed');
    }

    return true;
  } catch (e) {
    console.error('resendStaffInvite failed:', e);
    return false;
  }
};

export const disableStaffPersonalLogin = async (input: {
  clinicId: string;
  staffId: string;
  actedStaffId?: string;
}): Promise<boolean> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/disable-staff-personal-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'disable-staff-personal-login failed');
    }

    return true;
  } catch (e) {
    console.error('disableStaffPersonalLogin failed:', e);
    return false;
  }
};

export const enableStaffPersonalLogin = async (input: {
  clinicId: string;
  staffId: string;
  actedStaffId?: string;
}): Promise<boolean> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/enable-staff-personal-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'enable-staff-personal-login failed');
    }

    return true;
  } catch (e) {
    console.error('enableStaffPersonalLogin failed:', e);
    return false;
  }
};

export const changeStaffAuthEmail = async (input: {
  clinicId: string;
  staffId: string;
  email: string;
  actedStaffId?: string;
}): Promise<boolean> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/change-staff-auth-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'change-staff-auth-email failed');
    }

    return true;
  } catch (e) {
    console.error('changeStaffAuthEmail failed:', e);
    return false;
  }
};

/**
 * 特定のスタッフの情報を削除する
 * @param staffId 削除するスタッフのID
 */
export const getStaffDeletePreview = async (input: {
  staffId: string;
  clinicId: string;
  actedStaffId?: string;
}): Promise<StaffDeletePreview | null> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/delete-staff-preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText || 'delete-staff-preview failed';
      try {
        const parsed = JSON.parse(errorText);
        if (parsed?.error) {
          errorMessage = String(parsed.error);
        }
      } catch {
        // Keep the raw response text when JSON parsing is not possible.
      }
      throw new Error(errorMessage || 'delete-staff-preview failed');
    }

    return (await response.json()) as StaffDeletePreview;
  } catch (e) {
    console.error('getStaffDeletePreview failed:', e);
    return null;
  }
};

export const deleteStaff = async (input: {
  staffId: string;
  clinicId: string;
  actedStaffId?: string;
  replacementStaffId?: string;
  allowUnassigned?: boolean;
}): Promise<StaffDeleteResult> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/delete-staff`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText || 'delete-staff failed';
      try {
        const parsed = JSON.parse(errorText);
        if (parsed?.error) {
          errorMessage = String(parsed.error);
        }
      } catch {
        // Keep the raw response text when JSON parsing is not possible.
      }
      return { success: false, error: errorMessage || 'delete-staff failed' };
    }

    return { success: true };
  } catch (e) {
    console.error('deleteStaff failed:', e);
    return {
      success: false,
      error: e instanceof Error ? e.message : 'delete-staff failed',
    };
  }
};


/**
 * 新しい患者をデータベースに登録する
 * @param newPatientData 登録する患者のデータ
 */
export const addPatient = async (
  newPatientData: NewPatientData,
  currentUserId: string,
  clinicId?: string
): Promise<Patient | null> => {
  const operatorName = await getStaffSnapshotName(currentUserId);
  // TypeScriptの型定義（キャメルケース）をデータベースのカラム名（スネークケース）に変換
  const dbData: { [key: string]: any } = {
    name: newPatientData.name,
    name_romaji: newPatientData.nameRomaji,
    zipcode: newPatientData.zipcode,
    address: newPatientData.address,
    created_by: currentUserId,
    created_by_name: operatorName,
    updated_by: currentUserId,
    updated_by_name: operatorName,
    ...(clinicId ? { clinic_id: clinicId } : {}),
  };
  
  // 患者番号（新規登録時は自動生成された0000-0000形式）
  if (newPatientData.patientNumber !== undefined) {
    dbData.patient_number = newPatientData.patientNumber;
  }
  
  // 患者電話番号
  if (newPatientData.patientPhoneNumber !== undefined) {
    dbData.patient_phone_number = newPatientData.patientPhoneNumber;
  }
  
  // 保護者情報
  if (newPatientData.hasGuardian !== undefined) {
    dbData.has_guardian = newPatientData.hasGuardian;
  }
  if (newPatientData.hasGuardian && newPatientData.guardianName !== undefined) {
    dbData.guardian_name = newPatientData.guardianName;
  }
  if (newPatientData.hasGuardian && newPatientData.guardianNameRomaji !== undefined) {
    dbData.guardian_name_romaji = newPatientData.guardianNameRomaji;
  }
  if (newPatientData.hasGuardian && newPatientData.guardianContact !== undefined) {
    dbData.guardian_contact = newPatientData.guardianContact;
  }
  if (newPatientData.hasGuardian && newPatientData.guardianRelationship !== undefined) {
    dbData.guardian_relationship = newPatientData.guardianRelationship;
  }

  const { data, error } = await supabase
    .from('patients')
    .insert(dbData)
    .select()
    .single();

  if (error) {
    console.error('患者の追加に失敗しました:', error);
    return null;
  }

  // データベースから返されたデータをTypeScriptの型定義に変換
  const patient: Patient = {
    id: data.id,
    name: data.name,
    nameRomaji: data.name_romaji || '',
    isActive: data.is_active ?? true,
    patientNumber: data.patient_number || `P${String(data.id).padStart(3, '0')}`,
    phoneNumber: data.phone_number || '',
    patientPhoneNumber: data.patient_phone_number || '',
    birthDate: data.birth_date || null,
    zipcode: data.zipcode || '',
    address: data.address || '',
    hasGuardian: data.has_guardian || false,
    guardianName: data.guardian_name || '',
    guardianNameRomaji: data.guardian_name_romaji || '',
    guardianContact: data.guardian_contact || '',
    guardianRelationship: data.guardian_relationship || '',
    medicalHistory: '特になし',
    allergies: 'なし',
    emergencyContact: '未設定',
    notes: '',
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    createdBy: data.created_by || '不明',
    createdByName: data.created_by_name || data.created_by || '不明',
    updatedBy: data.updated_by || '不明',
    updatedByName: data.updated_by_name || data.updated_by || '不明',
    nextAppointment: '',
    doctorId: data.doctor_id || '',
  };

  return patient;
};

/**
 * 全ての患者情報をデータベースから取得する
 */
export const getAllPatients = async (clinicId?: string): Promise<Patient[]> => {
  let query = supabase
    .from('patients')
    .select('*')
    .order('created_at', { ascending: false });
  if (clinicId) {
    query = query.eq('clinic_id', clinicId);
  }
  const { data, error } = await query;
  
  if (error) {
    console.error('患者情報の取得に失敗しました:', error);
    return [];
  }
  
  // データベースのカラム名（スネークケース）をTypeScriptの型定義（キャメルケース）に変換
  return (data || []).map(patient => ({
    id: patient.id,
    name: patient.name,
    nameRomaji: patient.name_romaji || '',
    isActive: patient.is_active ?? true,
    patientNumber: patient.patient_number || `P${String(patient.id).padStart(3, '0')}`, // IDから生成
    phoneNumber: patient.phone_number || '',
    patientPhoneNumber: patient.patient_phone_number || '',
    birthDate: patient.birth_date || '',
    zipcode: patient.zipcode || '',
    address: patient.address || '',
    hasGuardian: patient.has_guardian || false,
    guardianName: patient.guardian_name || '',
    guardianNameRomaji: patient.guardian_name_romaji || '',
    guardianContact: patient.guardian_contact || '',
    guardianRelationship: patient.guardian_relationship || '',
    medicalHistory: '特になし', // デフォルト値
    allergies: 'なし', // デフォルト値
    emergencyContact: '未設定', // デフォルト値
    notes: '', // デフォルト値
    createdAt: patient.created_at,
    updatedAt: patient.updated_at,
    createdBy: patient.created_by || '不明',
    createdByName: patient.created_by_name || patient.created_by || '不明',
    updatedBy: patient.updated_by || '不明',
    updatedByName: patient.updated_by_name || patient.updated_by || '不明',
    nextAppointment: '', // デフォルト値
    doctorId: patient.doctor_id || '',
  })) as Patient[];
};

/**
 * 特定の患者の情報を更新する
 * @param patientId 更新する患者のID
 * @param updatedData 更新するデータ
 */
export const updatePatient = async (patientId: string, updatedData: Partial<Patient>): Promise<boolean> => {
  // フロントエンドのキャメルケースをDBのスネークケースに変換
  const dbData: { [key: string]: any } = {};
  if (updatedData.name) dbData.name = updatedData.name;
  if (updatedData.nameRomaji) dbData.name_romaji = updatedData.nameRomaji;
  if (updatedData.patientPhoneNumber !== undefined) dbData.patient_phone_number = updatedData.patientPhoneNumber;
  if (updatedData.hasGuardian !== undefined) dbData.has_guardian = updatedData.hasGuardian;
  if (updatedData.guardianName !== undefined) dbData.guardian_name = updatedData.guardianName;
  if (updatedData.guardianNameRomaji !== undefined) dbData.guardian_name_romaji = updatedData.guardianNameRomaji;
  if (updatedData.guardianContact !== undefined) dbData.guardian_contact = updatedData.guardianContact;
  if (updatedData.guardianRelationship !== undefined) dbData.guardian_relationship = updatedData.guardianRelationship;
  if (updatedData.zipcode) dbData.zipcode = updatedData.zipcode;
  if (updatedData.address) dbData.address = updatedData.address;
  if (updatedData.updatedBy) dbData.updated_by = updatedData.updatedBy;
  if (updatedData.updatedByName !== undefined) {
    dbData.updated_by_name = updatedData.updatedByName;
  } else if (updatedData.updatedBy) {
    const snapshotName = await getStaffSnapshotName(updatedData.updatedBy);
    if (snapshotName) {
      dbData.updated_by_name = snapshotName;
    }
  }
  dbData.updated_at = new Date().toISOString();

  const { error } = await supabase
    .from('patients')
    .update(dbData)
    .eq('id', patientId);

  if (error) {
    console.error('患者情報の更新に失敗しました:', error);
    return false;
  }
  
  return true;
};

/**
 * 特定の患者の情報を削除する
 * @param patientId 削除する患者のID
 */
export const deletePatient = async (patientId: string): Promise<boolean> => {
  // IMPORTANT:
  // We intentionally do NOT hard-delete patients because appointments/handovers may reference them.
  // Instead we soft-delete by setting is_active=false ("archive").
  const { error } = await supabase
    .from('patients')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', patientId);

  if (error) {
    console.error('患者情報のアーカイブに失敗しました:', error);
    // If schema is not migrated yet (missing column), do NOT fall back to hard delete.
    // Hard delete would recreate the broken state (appointments/handovers without patient row).
    return false;
  }

  return true;
};


// UUID生成のヘルパー関数（crypto.randomUUIDが利用できない場合の代替）
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // フォールバック: UUID v4を手動生成
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

export const pinLogin = async (pin: string, allStaff: Staff[]) => {
  // PINコードでスタッフを検索（引数のallStaffを使う）
  const staff = allStaff.find(s => String(s.pin) === String(pin) && s.isActive);

  if (!staff) {
    throw new Error('Invalid PIN');
  }

  // セッショントークンを生成（簡易版）
  const sessionToken = generateUUID();
  
  return {
    sessionToken,
    staff,
  };
};


/**
 * JST（日本標準時）で現在の日時を取得
 * date-fns-tzを使用してAsia/Tokyoで固定
 */
const getCurrentJSTDateTime = (): { date: string; time: string; datetime: Date } => {
  const now = new Date();
  const timeZone = 'Asia/Tokyo';
  
  // JSTで日付と時刻をフォーマット
  const dateStr = formatInTimeZone(now, timeZone, 'yyyy-MM-dd');
  const timeStr = formatInTimeZone(now, timeZone, 'HH:mm');
  
  return {
    date: dateStr,
    time: timeStr,
    datetime: now
  };
};

/**
 * バリデーション: 存在チェック（staff/patients）
 * 注意: doctor_idはtext型、staff.idはUUID型だが、Supabaseのeq()で比較時に
 * PostgreSQLが自動的にtext→UUIDキャストを行うため、直接比較可能
 */
const validateForeignKeys = async (
  doctorId: string,
  patientId: string
): Promise<AppointmentValidationError | null> => {
  // staff存在チェック（doctorId: string → staff.id: UUID）
  // PostgreSQLが自動的に型変換を行うため、直接比較可能
  const { data: staffData, error: staffError } = await supabase
    .from('staff')
    .select('id')
    .eq('id', doctorId)
    .eq('is_active', true)
    .single();
  
  if (staffError || !staffData) {
    return {
      code: 'FOREIGN_KEY_MISSING',
      message: 'スタッフまたは患者情報が見つかりません'
    };
  }
  
  // patients存在チェック（patientId: string → patients.id: textまたはinteger）
  // patients.idの型によっては、文字列比較で十分
  const { data: patientData, error: patientError } = await supabase
    .from('patients')
    .select('id')
    .eq('id', patientId)
    .single();
  
  if (patientError || !patientData) {
    return {
      code: 'FOREIGN_KEY_MISSING',
      message: 'スタッフまたは患者情報が見つかりません'
    };
  }
  
  return null;
};

/**
 * バリデーション: 過去日時チェック
 * 当日飛び込み受診に対応するため、当日は時刻に関係なく許可
 */
const validatePastDateTime = (
  appointmentDate: string,
  appointmentTime: string
): AppointmentValidationError | null => {
  const jstNow = getCurrentJSTDateTime();
  
  // 過去の日付（今日より前）の場合は拒否
  if (appointmentDate < jstNow.date) {
    return {
      code: 'PAST_DATETIME',
      message: '過去の日時は選択できません'
    };
  }
  
  // 今日の日付の場合は、時刻に関係なく許可（当日飛び込み受診に対応）
  // 未来の日付の場合は許可（既存の動作を維持）
  
  return null;
};

/**
 * バリデーション: 診療時間外チェック（9:00-18:00）
 */
const validateBusinessHours = (appointmentTime: string): AppointmentValidationError | null => {
  const [hours, minutes] = appointmentTime.split(':').map(Number);
  const timeInMinutes = hours * 60 + minutes;
  const openTime = 9 * 60; // 9:00
  const closeTime = 18 * 60; // 18:00
  
  if (timeInMinutes < openTime || timeInMinutes >= closeTime) {
    return {
      code: 'OUT_OF_BUSINESS_HOURS',
      message: '診療時間外です（9:00-18:00）'
    };
  }
  
  return null;
};

/**
 * バリデーション: スロット整列チェック（30分刻み）
 */
const validateSlotAlignment = (appointmentTime: string): AppointmentValidationError | null => {
  const timePattern = /^([0-1]?[0-9]|2[0-3]):([03]0)$/;
  if (!timePattern.test(appointmentTime)) {
    return {
      code: 'SLOT_MISALIGNED',
      message: '30分刻みの時刻を選択してください'
    };
  }
  
  return null;
};

/**
 * バリデーション: ダブルブッキングチェック
 */
const validateDoubleBooking = async (
  doctorId: string,
  appointmentDate: string,
  appointmentTime: string
): Promise<AppointmentValidationError | null> => {
  const { data, error } = await supabase
    .from('appointments')
    .select('id')
    .eq('doctor_id', doctorId)
    .eq('appointment_date', appointmentDate)
    .eq('appointment_time', appointmentTime)
    .limit(1);
  
  if (error) {
    // エラーが発生した場合は、ダブルブッキングチェックをスキップ（DB制約で防がれる）
    return null;
  }
  
  if (data && data.length > 0) {
    return {
      code: 'DOUBLE_BOOKED',
      message: 'この時間枠は既に予約されています'
    };
  }
  
  return null;
};

/**
 * バリデーション: 同じ患者の同じ日チェック（スタッフ間でも確認）
 */
const validateSamePatientSameDate = async (
  patientId: string,
  appointmentDate: string
): Promise<AppointmentValidationError | null> => {
  const { data, error } = await supabase
    .from('appointments')
    .select('id')
    .eq('patient_id', patientId)
    .eq('appointment_date', appointmentDate)
    .limit(1);
  
  if (error) {
    // エラーが発生した場合は、チェックをスキップ
    console.warn('同じ患者・同じ日のチェック中にエラーが発生:', error);
    return null;
  }
  
  if (data && data.length > 0) {
    return {
      code: 'SAME_PATIENT_SAME_DATE',
      message: 'この患者は既に同じ日に予約が登録されています'
    };
  }
  
  return null;
};

/**
 * 予約データのバリデーション
 */
const validateAppointmentData = async (
  doctorId: string,
  patientId: string,
  appointmentDate: string,
  appointmentTime: string
): Promise<AppointmentValidationError | null> => {
  // 1. 存在チェック
  const fkError = await validateForeignKeys(doctorId, patientId);
  if (fkError) return fkError;
  
  // 2. 過去日時チェック
  const pastError = validatePastDateTime(appointmentDate, appointmentTime);
  if (pastError) return pastError;
  
  // 3. 診療時間外チェック
  const hoursError = validateBusinessHours(appointmentTime);
  if (hoursError) return hoursError;
  
  // 4. スロット整列チェック
  const slotError = validateSlotAlignment(appointmentTime);
  if (slotError) return slotError;
  
  // 5. 同じ患者の同じ日チェック（スタッフ間でも確認）
  const samePatientError = await validateSamePatientSameDate(patientId, appointmentDate);
  if (samePatientError) return samePatientError;
  
  // 6. ダブルブッキングチェック
  const doubleError = await validateDoubleBooking(doctorId, appointmentDate, appointmentTime);
  if (doubleError) return doubleError;
  
  return null;
};

/**
 * DB応答型（AppointmentRow）からフロントエンド型（Appointment）にマッピング
 */
const mapAppointmentRowToAppointment = (row: AppointmentRow): Appointment => {
  return {
    id: row.id,
    doctorId: row.doctor_id,
    doctorName: row.doctor_name || '',
    patientId: row.patient_id,
    appointmentTime: row.appointment_time,
    appointmentDate: row.appointment_date,
    type: '', // DBにはtypeフィールドがないため、空文字列
    status: row.status as 'scheduled' | 'completed' | 'cancelled',
    notes: row.notes || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
};

// Safe creator (RLS前提): direct Supabase insert
export const createAppointmentSafe = async (
  appointmentData: {
    doctorId: string;
    patientId: string;
    appointmentDate: string; // YYYY-MM-DD
    appointmentTime: string; // HH:mm
    status?: string;
    notes?: string;
    type?: string;
    clinicId?: string;
  }
): Promise<Appointment> => {
  // バリデーション実行
  const validationError = await validateAppointmentData(
    appointmentData.doctorId,
    appointmentData.patientId,
    appointmentData.appointmentDate,
    appointmentData.appointmentTime
  );
  
  if (validationError) {
    // AppointmentErrorで統一
    throw new AppointmentError(
      validationError.code,
      validationError.message,
      validationError
    );
  }
  
  // バリデーション通過後、直接 appointments テーブルへ保存
  const doctorName = await getStaffSnapshotName(appointmentData.doctorId);
  const dbRow: AppointmentRowInput & { clinic_id?: string; doctor_id_uuid?: string | null } = {
    doctor_id: appointmentData.doctorId,
    doctor_name: doctorName ?? '',
    doctor_id_uuid: appointmentData.doctorId,
    patient_id: appointmentData.patientId,
    appointment_date: appointmentData.appointmentDate,
    appointment_time: appointmentData.appointmentTime,
    status: appointmentData.status ?? 'scheduled',
    notes: appointmentData.notes ?? '',
    ...(appointmentData.clinicId ? { clinic_id: appointmentData.clinicId } : {}),
  };
  const { data, error } = await supabase
    .from('appointments')
    .insert(dbRow)
    .select()
    .single();
  if (error) {
    // DB制約エラー（例: ユニーク制約違反）もチェック
    if (error.code === '23505') { // PostgreSQL unique violation
      const validationErr: AppointmentValidationError = {
        code: 'DOUBLE_BOOKED',
        message: 'この時間枠は既に予約されています'
      };
      throw new AppointmentError(
        'DOUBLE_BOOKED',
        'この時間枠は既に予約されています',
        validationErr
      );
    }
    // その他のDBエラーは一般的なエラーとして扱う
    throw new AppointmentError(
      'FOREIGN_KEY_MISSING',
      error.message || '予約の作成に失敗しました'
    );
  }
  
  if (!data) {
    throw new AppointmentError(
      'FOREIGN_KEY_MISSING',
      '予約の作成に失敗しました'
    );
  }
  
  // DB応答をフロントエンド型にマッピング
  return mapAppointmentRowToAppointment(data as AppointmentRow);
};

export const getAppointmentsDirect = async (
  dateYYYYMMDD: string,
  doctorId?: string,
  clinicId?: string
): Promise<AppointmentRow[]> => {
  let query = supabase
    .from('appointments')
    .select('*')
    .eq('appointment_date', dateYYYYMMDD);
  if (doctorId) {
    query = query.eq('doctor_id', doctorId);
  }
  if (clinicId) {
    query = query.eq('clinic_id', clinicId);
  }
  const { data, error } = await query.order('appointment_time', { ascending: true });
  if (error) throw error;
  return (data as AppointmentRow[]) || [];
};

/** 期間内に予約が1件以上ある日付（YYYY-MM-DD）を重複なしで返す。キャンセルは除外 */
export const getBookedAppointmentDatesInRange = async (
  dateFromYYYYMMDD: string,
  dateToYYYYMMDD: string,
  doctorId?: string,
  clinicId?: string
): Promise<string[]> => {
  let query = supabase
    .from('appointments')
    .select('appointment_date, status')
    .gte('appointment_date', dateFromYYYYMMDD)
    .lte('appointment_date', dateToYYYYMMDD);
  if (doctorId) {
    query = query.eq('doctor_id', doctorId);
  }
  if (clinicId) {
    query = query.eq('clinic_id', clinicId);
  }
  const { data, error } = await query;
  if (error) {
    console.error('予約日一覧の取得に失敗しました:', error);
    throw error;
  }
  const uniq = new Set<string>();
  for (const row of data || []) {
    const appointment = row as { appointment_date?: string; status?: string | null };
    if (appointment.status === 'cancelled') continue;
    const d = appointment.appointment_date;
    if (d) uniq.add(d);
  }
  return Array.from(uniq);
};

export const getUnassignedFutureAppointmentsCount = async (
  clinicId?: string
): Promise<number> => {
  if (!clinicId) return 0;
  const today = formatInTimeZone(new Date(), 'Asia/Tokyo', 'yyyy-MM-dd');
  const { count, error } = await supabase
    .from('appointments')
    .select('id', { count: 'exact', head: true })
    .eq('clinic_id', clinicId)
    .eq('doctor_id', '')
    .neq('status', 'cancelled')
    .gte('appointment_date', today);

  if (error) {
    console.error('担当未設定予約の取得に失敗しました:', error);
    return 0;
  }

  return count ?? 0;
};

export const deleteAppointmentByKey = async (
  dateYYYYMMDD: string,
  timeHHmm: string,
  doctorId: string,
  clinicId?: string
): Promise<boolean> => {
  let query = supabase
    .from('appointments')
    .delete()
    .eq('appointment_date', dateYYYYMMDD)
    .eq('appointment_time', timeHHmm)
    .eq('doctor_id', doctorId);
  if (clinicId) {
    query = query.eq('clinic_id', clinicId);
  }
  const { error } = await query;
  if (error) {
    console.error('予約削除に失敗しました:', error);
    return false;
  }
  return true;
};

export const subscribeAppointments = (
  onChange: () => void,
  clinicId?: string
) => {
  // 予約テーブルの変更を監視（簡易: 全件）
  const filter = clinicId
    ? { event: '*', schema: 'public', table: 'appointments', filter: `clinic_id=eq.${clinicId}` }
    : { event: '*', schema: 'public', table: 'appointments' };
  const channel = (supabase as any).channel('appointments_changes')
    .on('postgres_changes', filter as any, () => {
      try { onChange(); } catch { /* noop */ }
    })
    .subscribe();
  return () => {
    try { (supabase as any).removeChannel(channel); } catch { /* noop */ }
  };
};


/**
 * 全ての申し送りをデータベースから取得する
 * @param patientId オプション: 特定の患者の申し送りのみ取得する場合
 */
export const getAllHandovers = async (patientId?: string, clinicId?: string): Promise<Handover[]> => {
  let query = supabase
    .from('handovers')
    .select('*')
    .order('created_at', { ascending: false });

  // 特定の患者の申し送りのみ取得する場合
  if (patientId) {
    query = query.eq('patient_id', patientId);
  }
  if (clinicId) {
    query = query.eq('clinic_id', clinicId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('申し送りの取得に失敗しました:', error);
    return [];
  }

  // 患者名を取得するため、患者リストも取得
  let patientsQuery = supabase
    .from('patients')
    .select('id, name');
  if (clinicId) {
    patientsQuery = patientsQuery.eq('clinic_id', clinicId);
  }
  const { data: patientsData } = await patientsQuery;

  const patientsMap = new Map<string, string>();
  if (patientsData) {
    patientsData.forEach((p: any) => {
      patientsMap.set(p.id, p.name);
    });
  }

  // データベースのカラム名（スネークケース）をTypeScriptの型定義（キャメルケース）に変換
  return (data || []).map((row: any) => ({
    id: row.id,
    patientId: row.patient_id,
    patientName: patientsMap.get(row.patient_id) || '', // 患者名をマップから取得
    content: row.content,
    createdBy: row.created_by || '',
    createdByName: row.created_by_name || row.created_by || '不明',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    isCompleted: row.is_completed || false,
  })) as Handover[];
};

/**
 * 特定の患者の申し送りを取得する
 */
export const getHandoversByPatientId = async (patientId: string): Promise<Handover[]> => {
  return getAllHandovers(patientId);
};

/**
 * 新しい申し送りをデータベースに登録する
 */
export const createHandoverDB = async (
  handoverData: {
    patientId: string;
    content: string;
    createdBy?: string;
    clinicId?: string;
  }
): Promise<Handover | null> => {
  const creatorName = await getStaffSnapshotName(handoverData.createdBy);
  // TypeScriptの型定義（キャメルケース）をデータベースのカラム名（スネークケース）に変換
  const dbData: { [key: string]: any } = {
    patient_id: handoverData.patientId,
    content: handoverData.content,
    is_completed: false,
    ...(handoverData.clinicId ? { clinic_id: handoverData.clinicId } : {}),
  };

  if (handoverData.createdBy) {
    dbData.created_by = handoverData.createdBy;
    dbData.created_by_name = creatorName;
  }

  const { data, error } = await supabase
    .from('handovers')
    .insert(dbData)
    .select('*')
    .single();

  if (error) {
    console.error('申し送りの追加に失敗しました:', error);
    return null;
  }

  // 患者名を取得
  const { data: patientData } = await supabase
    .from('patients')
    .select('name')
    .eq('id', handoverData.patientId)
    .single();

  // データベースから返されたデータをTypeScriptの型定義に変換
  const handover: Handover = {
    id: data.id,
    patientId: data.patient_id,
    patientName: patientData?.name || '',
    content: data.content,
    createdBy: data.created_by || '',
    createdByName: data.created_by_name || data.created_by || '不明',
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    isCompleted: data.is_completed || false,
  };

  return handover;
};

/**
 * 特定の申し送りの情報を更新する
 */
export const updateHandoverDB = async (
  handoverId: string,
  updatedData: {
    content?: string;
    isCompleted?: boolean;
  }
): Promise<boolean> => {
  // camelCase から snake_case への変換
  const dbData: { [key: string]: any } = {};
  if (updatedData.content !== undefined) dbData.content = updatedData.content;
  if (updatedData.isCompleted !== undefined) dbData.is_completed = updatedData.isCompleted;
  dbData.updated_at = new Date().toISOString(); // 更新日は必ず更新

  const { error } = await supabase
    .from('handovers')
    .update(dbData)
    .eq('id', handoverId);

  if (error) {
    console.error('申し送りの更新に失敗しました:', error);
    return false;
  }

  return true;
};

/**
 * 特定の申し送りの情報を削除する
 */
export const deleteHandoverDB = async (handoverId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('handovers')
    .delete()
    .eq('id', handoverId);

  if (error) {
    console.error('申し送りの削除に失敗しました:', error);
    return false;
  }

  return true;
};

// =========================================================
// clinic_speech_terms（医院別・音声用語辞書）
// =========================================================

const mapClinicSpeechTermRow = (row: Record<string, unknown>): ClinicSpeechTerm => ({
  id: String(row.id),
  clinicId: String(row.clinic_id ?? ''),
  phrase: String(row.phrase ?? ''),
  reading: String(row.reading ?? ''),
  createdAt: String(row.created_at ?? ''),
  updatedAt: String(row.updated_at ?? ''),
  createdByStaffId: row.created_by_staff_id != null ? String(row.created_by_staff_id) : null,
});

/** 同一タブ内で辞書 REST を何度も叩かない（initialize と辞書パネル開閉の重複で 404 が連打されるのを防ぐ） */
const clinicSpeechTermsSessionCache = new Map<string, ClinicSpeechTerm[]>();
const clinicSpeechTermsInFlight = new Map<string, Promise<ClinicSpeechTerm[]>>();

export function clearClinicSpeechTermsSessionCache(): void {
  clinicSpeechTermsSessionCache.clear();
  clinicSpeechTermsInFlight.clear();
}

export type GetClinicSpeechTermsOptions = { refresh?: boolean };

export const getClinicSpeechTerms = async (
  clinicId?: string,
  options?: GetClinicSpeechTermsOptions
): Promise<ClinicSpeechTerm[]> => {
  const key = clinicId ?? '__all__';
  if (!options?.refresh && clinicSpeechTermsSessionCache.has(key)) {
    return clinicSpeechTermsSessionCache.get(key)!;
  }
  if (!options?.refresh) {
    const inflight = clinicSpeechTermsInFlight.get(key);
    if (inflight) return inflight;
  }

  const run = (async () => {
    let query = supabase.from('clinic_speech_terms').select('*').order('created_at', { ascending: false });
    if (clinicId) {
      query = query.eq('clinic_id', clinicId);
    }
    const { data, error } = await query;

    if (error) {
      clinicSpeechTermsSessionCache.set(key, []);
      return [];
    }

    const terms = (data || []).map((row) => mapClinicSpeechTermRow(row as Record<string, unknown>));
    clinicSpeechTermsSessionCache.set(key, terms);
    return terms;
  })();

  if (!options?.refresh) {
    clinicSpeechTermsInFlight.set(key, run);
  }
  try {
    return await run;
  } finally {
    if (!options?.refresh) {
      clinicSpeechTermsInFlight.delete(key);
    }
  }
};

export const insertClinicSpeechTerm = async (
  phrase: string,
  reading: string,
  clinicId: string | undefined,
  createdByStaffId: string | null
): Promise<ClinicSpeechTerm | null> => {
  if (!clinicId) return null;
  const { data, error } = await supabase
    .from('clinic_speech_terms')
    .insert({
      clinic_id: clinicId,
      phrase: phrase.trim(),
      reading: reading.trim(),
      created_by_staff_id: createdByStaffId,
    })
    .select()
    .single();

  if (error) {
    console.error('音声用語の登録に失敗しました:', error);
    return null;
  }

  return mapClinicSpeechTermRow(data as Record<string, unknown>);
};

export const updateClinicSpeechTerm = async (
  termId: string,
  updates: { phrase?: string; reading?: string }
): Promise<boolean> => {
  const dbData: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.phrase !== undefined) dbData.phrase = updates.phrase.trim();
  if (updates.reading !== undefined) dbData.reading = updates.reading.trim();

  const { error } = await supabase.from('clinic_speech_terms').update(dbData).eq('id', termId);

  if (error) {
    console.error('音声用語の更新に失敗しました:', error);
    return false;
  }

  return true;
};

export const deleteClinicSpeechTerm = async (termId: string): Promise<boolean> => {
  const { error } = await supabase.from('clinic_speech_terms').delete().eq('id', termId);

  if (error) {
    console.error('音声用語の削除に失敗しました:', error);
    return false;
  }

  return true;
};

// =========================================================
// speech_error_reports（音声入力の誤変換報告）
// =========================================================

export const insertSpeechErrorReport = async (params: {
  clinicId: string;
  reportedByStaffId: string | null;
  transcribedText: string;
}): Promise<boolean> => {
  const { error } = await supabase.from('speech_error_reports').insert({
    clinic_id: params.clinicId,
    reported_by_staff_id: params.reportedByStaffId,
    transcribed_text: params.transcribedText.trim(),
  });

  if (error) {
    console.error('誤変換報告の送信に失敗しました:', error);
    return false;
  }

  return true;
};